import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  GridLegacy as Grid,
  Typography,
  Alert,
  Box,
  Chip,
  FormControlLabel,
  Switch,
  InputAdornment,
} from '@mui/material';
import { EmojiEvents as TrophyIcon, CalendarToday as CalendarIcon } from '@mui/icons-material';
import { Timestamp } from 'firebase/firestore';
import { Mission } from '../../types';
import { missionService } from '../../services/missionService';

interface CreateMissionDialogProps {
  open: boolean;
  onClose: () => void;
  onMissionCreated: (mission: Mission) => void;
  teamId: string;
}

const CreateMissionDialog: React.FC<CreateMissionDialogProps> = ({
  open,
  onClose,
  onMissionCreated,
  teamId,
}) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    pointsReward: 10,
    type: 'distance' as 'distance' | 'consistency' | 'improvement' | 'team',
    isActive: true,
    startDate: new Date(),
    endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // 30 days from now
    requirements: {
      totalDistance: 0,
      consecutiveDays: 0,
      improvementPercentage: 0,
      teamGoal: '',
    },
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      // Reset form when dialog opens
      const now = new Date();
      const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
      
      setFormData({
        title: '',
        description: '',
        pointsReward: 10,
        type: 'distance',
        isActive: true,
        startDate: now,
        endDate: thirtyDaysFromNow,
        requirements: {
          totalDistance: 0,
          consecutiveDays: 0,
          improvementPercentage: 0,
          teamGoal: '',
        },
      });
      setError('');
    }
  }, [open]);

  const handleInputChange = (field: string) => (event: any) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDateChange = (field: string) => (event: any) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: new Date(value)
    }));
  };

  const handleRequirementsChange = (field: string) => (event: any) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      requirements: {
        ...prev.requirements,
        [field]: value
      }
    }));
  };

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      setError('Mission title is required');
      return;
    }

    if (!formData.description.trim()) {
      setError('Mission description is required');
      return;
    }

    if (formData.pointsReward !== 10) {
      setError('Mission points reward must be 10 points');
      return;
    }

    if (formData.endDate <= formData.startDate) {
      setError('End date must be after start date');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const missionData = {
        teamId,
        title: formData.title.trim(),
        description: formData.description.trim(),
        pointsReward: formData.pointsReward,
        type: formData.type,
        isActive: formData.isActive,
        startDate: Timestamp.fromDate(formData.startDate),
        endDate: Timestamp.fromDate(formData.endDate),
        requirements: formData.requirements,
      };

      const missionId = await missionService.createMission(missionData);
      
      const newMission: Mission = {
        id: missionId,
        ...missionData,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };

      onMissionCreated(newMission);
      onClose();
    } catch (err: any) {
      console.error('Error creating mission:', err);
      setError(err.message || 'Failed to create mission');
    } finally {
      setLoading(false);
    }
  };

  const getMissionTypeDescription = (type: string) => {
    switch (type) {
      case 'distance':
        return 'Mission focused on achieving total distance goals';
      case 'consistency':
        return 'Mission focused on maintaining consistent activity';
      case 'improvement':
        return 'Mission focused on improving performance over time';
      case 'team':
        return 'Mission focused on team-based achievements';
      default:
        return '';
    }
  };

  const getRequirementsForType = (type: string) => {
    switch (type) {
      case 'distance':
        return ['totalDistance'];
      case 'consistency':
        return ['consecutiveDays'];
      case 'improvement':
        return ['improvementPercentage'];
      case 'team':
        return ['teamGoal'];
      default:
        return [];
    }
  };

  const requiredFields = getRequirementsForType(formData.type);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TrophyIcon color="primary" />
          <Typography variant="h6">Create New Mission</Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Basic Information
            </Typography>
          </Grid>

          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              label="Mission Title"
              value={formData.title}
              onChange={handleInputChange('title')}
              placeholder="e.g., Marathon Training Mission"
              required
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Points Reward"
              type="number"
              value={formData.pointsReward}
              onChange={handleInputChange('pointsReward')}
              inputProps={{ min: 10, max: 10 }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><TrophyIcon /></InputAdornment>,
              }}
              helperText="Fixed at 10 points"
              disabled
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Description"
              value={formData.description}
              onChange={handleInputChange('description')}
              placeholder="Describe the mission objectives and what athletes need to achieve..."
              required
            />
          </Grid>

          {/* Mission Type */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Mission Type</InputLabel>
              <Select
                value={formData.type}
                onChange={handleInputChange('type')}
                label="Mission Type"
              >
                <MenuItem value="distance">Distance Mission</MenuItem>
                <MenuItem value="consistency">Consistency Mission</MenuItem>
                <MenuItem value="improvement">Improvement Mission</MenuItem>
                <MenuItem value="team">Team Mission</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {getMissionTypeDescription(formData.type)}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                  />
                }
                label="Active Mission"
              />
              <Chip 
                label="10 points" 
                color="error"
                size="small"
              />
            </Box>
          </Grid>

          {/* Date Range */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Start Date"
              type="date"
              value={formData.startDate.toISOString().split('T')[0]}
              onChange={handleDateChange('startDate')}
              InputProps={{
                startAdornment: <InputAdornment position="start"><CalendarIcon /></InputAdornment>,
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="End Date"
              type="date"
              value={formData.endDate.toISOString().split('T')[0]}
              onChange={handleDateChange('endDate')}
              InputProps={{
                startAdornment: <InputAdornment position="start"><CalendarIcon /></InputAdornment>,
              }}
            />
          </Grid>

          {/* Requirements */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Mission Requirements
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Set the specific requirements for this mission type.
            </Typography>
          </Grid>

          {requiredFields.includes('totalDistance') && (
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Total Distance Goal (miles)"
                type="number"
                value={formData.requirements.totalDistance}
                onChange={handleRequirementsChange('totalDistance')}
                inputProps={{ min: 0, step: 0.1 }}
                placeholder="e.g., 26.2 for marathon"
                required
              />
            </Grid>
          )}

          {requiredFields.includes('consecutiveDays') && (
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Consecutive Days Required"
                type="number"
                value={formData.requirements.consecutiveDays}
                onChange={handleRequirementsChange('consecutiveDays')}
                inputProps={{ min: 1 }}
                placeholder="e.g., 30 for 30-day streak"
                required
              />
            </Grid>
          )}

          {requiredFields.includes('improvementPercentage') && (
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Improvement Percentage (%)"
                type="number"
                value={formData.requirements.improvementPercentage}
                onChange={handleRequirementsChange('improvementPercentage')}
                inputProps={{ min: 0, max: 100 }}
                placeholder="e.g., 10 for 10% improvement"
                required
              />
            </Grid>
          )}

          {requiredFields.includes('teamGoal') && (
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Team Goal Description"
                value={formData.requirements.teamGoal}
                onChange={handleRequirementsChange('teamGoal')}
                placeholder="e.g., Complete 1000 miles as a team"
                required
              />
            </Grid>
          )}
        </Grid>
      </DialogContent>
      
      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !formData.title.trim() || !formData.description.trim()}
          startIcon={<TrophyIcon />}
        >
          {loading ? 'Creating...' : 'Create Mission'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateMissionDialog;
